import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import {
  BundleOnsiteUpsellComponentSchema,
  SharedBundleOnsiteUpsellComponent,
  SharedBundleOnsiteUpsellComponentSchema,
} from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { ONSITE_UPSELL_TEMPLATES } from "@intelligems/ig-utils/onsite-upsell-template";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import {
  DEFAULT_PLACEMENT,
  placementForMode,
  placementMode,
  upsellConfigFields,
  upsellErrors,
  upsellModFields,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsellModHandler";
import { flatRulesToCanonical } from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsell/ruleMapping";
import type {
  BundleOnsiteUpsellComponent,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";

const meaningful = (mod: BundleOnsiteUpsellComponent) => ({
  location: mod.location,
  anchorKey: mod.anchorKey,
  selector: mod.selector,
  insertType: mod.insertType,
  maxProducts: mod.maxProducts,
  excludeOutOfStock: mod.excludeOutOfStock,
  excludeItemsInCart: mod.excludeItemsInCart,
  excludePdpProduct: mod.excludePdpProduct,
  allowItemSelection: mod.allowItemSelection,
  copy: mod.copy,
  discount: mod.discount,
  rules: mod.rules,
  template: mod.template,
  description: mod.description,
  agenticLogicRuleId: mod.agenticLogicRuleId,
});

export const bundleOnsiteUpsellModHandler: ModHandler<BundleOnsiteUpsellComponent> =
  {
    normalize: (mod) => ({
      ...mod,
      anchorKey: mod.anchorKey?.trim() || undefined,
      selector: mod.selector?.trim(),
    }),

    convertFrom: (mod) => ({
      type: "component",
      dto: {
        id: mod.id,
        variationId: mod.variationId,
        config: {
          type: "bundleOnsiteUpsell",
          ...upsellConfigFields(mod),
          template: mod.template,
          copy: mod.copy,
          maxProductLimit: mod.maxProducts,
          allowItemSelection: mod.allowItemSelection,
          excludeOutOfStock: mod.excludeOutOfStock,
          excludeItemsInCart: mod.excludeItemsInCart,
          excludePdpProduct: mod.excludePdpProduct,
          ...(mod.discount ? { discount: mod.discount } : {}),
        },
      },
    }),

    convertTo: (conversion) => {
      if (conversion.type !== "component") {
        throw new Error("Expected component conversion");
      }
      const { id, variationId, config } = conversion.dto;
      if (config.type !== "bundleOnsiteUpsell") {
        throw new Error(
          `Expected bundleOnsiteUpsell config, got ${config.type}`
        );
      }
      return {
        type: "bundleOnsiteUpsell",
        category: "component",
        id,
        variationId,
        ...upsellModFields(config),
        maxProducts: config.maxProductLimit,
        allowItemSelection: config.allowItemSelection,
        excludeOutOfStock: config.excludeOutOfStock,
        excludeItemsInCart: config.excludeItemsInCart,
        excludePdpProduct: config.excludePdpProduct,
        copy: config.copy,
        discount:
          config.discount && config.discount.discountType !== "none"
            ? config.discount
            : null,
        template: config.template,
      };
    },

    getErrors: (mod) =>
      upsellErrors(
        BundleOnsiteUpsellComponentSchema.safeParse(
          bundleOnsiteUpsellModHandler.toAiFormat(mod)
        ),
        mod
      ),

    hasChanges: (oldMod, newMod) =>
      !isEqual(meaningful(oldMod), meaningful(newMod)),

    isEquivalent: (a, b) =>
      b.type === "bundleOnsiteUpsell" &&
      a.location === b.location &&
      a.anchorKey === b.anchorKey &&
      a.selector === b.selector,

    defaults: () => {
      const rules: BundleOnsiteUpsellComponent["rules"] = [
        { conditions: [], products: { source: "recommendations" } },
      ];
      return {
        id: v4(),
        type: "bundleOnsiteUpsell",
        category: "component",
        variationId: "",
        location: "cart",
        selector: DEFAULT_PLACEMENT.cart.selector,
        insertType: DEFAULT_PLACEMENT.cart.insertType,
        maxProducts: 3,
        allowItemSelection: true,
        excludeOutOfStock: true,
        excludeItemsInCart: true,
        excludePdpProduct: true,
        copy: { heading: "Bundle and save", buttonText: "Add to cart" },
        discount: null,
        rules,
        canonicalRules: flatRulesToCanonical(rules, "cart"),
        template: ONSITE_UPSELL_TEMPLATES.bundle,
      };
    },

    toAiFormat: (mod) =>
      pickSharedFields<SharedBundleOnsiteUpsellComponent>(
        SharedBundleOnsiteUpsellComponentSchema,
        mod
      ),

    fromAiFormat: (aiMod, context) => {
      const merged = {
        ...(context.existing ?? bundleOnsiteUpsellModHandler.defaults()),
        ...aiMod,
        ...sharedFromAiFormat(aiMod, context),
        category: "component" as const,
      };
      const base = {
        ...merged,
        ...placementForMode(placementMode(aiMod), merged.location, aiMod),
      };
      return {
        ...base,
        canonicalRules: flatRulesToCanonical(
          base.rules,
          base.location,
          context.existing?.canonicalRules
        ),
      };
    },
  };
